import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import profileAPI from '../api/profile';
import CompanyAutocomplete from '../components/common/CompanyAutocomplete';

const Profile = () => {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isDirty } } = useForm();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [currentCompany, setCurrentCompany] = useState('');
  
  // Watch for form changes including the company field
  const watchedFields = watch(['gender', 'about_me', 'current_company']);
  
  // Check if form has changes (including company field)
  const hasChanges = isDirty || (profile && currentCompany !== (profile.current_company || ''));
  
  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsFetching(true);
        setError('');
        
        // Get profile with user data included
        const response = await profileAPI.getProfile(currentUser.applicant_id);
        
        // If user data is not included in the response, fetch it separately
        if (!response.data.user && currentUser) {
          try {
            const userResponse = await profileAPI.getUserData();
            response.data.user = userResponse.data;
          } catch (userErr) {
            console.error('Failed to fetch user data:', userErr);
            // Use currentUser as fallback
            response.data.user = currentUser;
          }
        }
        
        setProfile(response.data);
        
        // Set form default values
        reset({
          gender: response.data.gender || '',
          about_me: response.data.about_me || '',
          current_company: response.data.current_company || ''
        });
        
        // Set current company state
        setCurrentCompany(response.data.current_company || '');
      } catch (err) {
        if (err.response?.data?.error) {
          setError(err.response.data.error);
        } else {
          setError('שגיאה בטעינת פרופיל. אנא נסה שנית.');
        }
        console.error(err);
      } finally {
        setIsFetching(false);
      }
    };
    
    if (currentUser?.applicant_id) {
      fetchProfile();
    }
  }, [currentUser, reset]);
  
  // Handle company change from autocomplete
  const handleCompanyChange = (companyName) => {
    setCurrentCompany(companyName);
    setValue('current_company', companyName, { shouldDirty: true });
  };

  // Handle profile update
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      // Include current company in the data
      const submitData = {
        ...data,
        current_company: currentCompany
      };
      
      await profileAPI.updateProfile(currentUser.applicant_id, submitData);
      
      setSuccess('הפרופיל עודכן בהצלחה');
      
      // Update local profile state
      setProfile(prev => ({
        ...prev,
        ...submitData
      }));
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בעדכון פרופיל. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">פרופיל אישי</h1>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        {isFetching ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="card">
            {profile && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">פרטים אישיים</h2>
                
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6">
                  <div>
                    <p className="text-sm text-gray-500">שם פרטי</p>
                    <p className="font-medium">{profile.user?.first_name || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">שם משפחה</p>
                    <p className="font-medium">{profile.user?.last_name || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">אימייל</p>
                    <p className="font-medium">{profile.user?.email || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">טלפון</p>
                    <p className="font-medium">{profile.user?.phone_number || '-'}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <h2 className="text-lg font-semibold mb-4">עדכון פרטים</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="gender" className="form-label">מגדר</label>
                  <select
                    id="gender"
                    {...register('gender')}
                    className="form-input"
                  >
                    <option value="">בחר מגדר</option>
                    <option value="male">זכר</option>
                    <option value="female">נקבה</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="about_me" className="form-label">על עצמי - מקצועי</label>
                  <textarea
                    id="about_me"
                    {...register('about_me')}
                    rows={4}
                    className="form-input"
                    placeholder="ספר/י לנו על עצמך ועל הניסיון המקצועי שלך בהרחבה (בדומה לקורות החיים שלך, אבל בשפה חופשית) - כדאי להרחיב על מקומות העבודה, השגים ותחומי התמחות"
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="current_company" className="form-label">מעסיק נוכחי</label>
                  <CompanyAutocomplete
                    value={currentCompany}
                    onChange={handleCompanyChange}
                    placeholder="שם המעסיק בו את/ה עובד/ת כיום"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading || !hasChanges}
                    className={`btn-primary ${(isLoading || !hasChanges) ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? 'מעדכן...' : 'עדכן פרופיל'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
